import React, { useState, useEffect, useMemo } from 'react';
import { auditApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Search, Shield, LogIn, LogOut, UserPlus, CalendarOff, Receipt, FileEdit } from 'lucide-react';

const actionIcons = {
  Login: LogIn,
  Logout: LogOut,
  'Employee Added': UserPlus,
  'Employee Updated': FileEdit,
  'Leave Applied': CalendarOff,
  'Leave Approved': CalendarOff,
  'Leave Rejected': CalendarOff,
  'Pay Run Created': Receipt,
  'Pay Run Frozen': Receipt,
  'Pay Run Processed': Receipt,
};

export default function AuditTrailPage() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [availableActions, setAvailableActions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const normalizeListResponse = (value) => {
    if (Array.isArray(value)) return { items: value, total: value.length, page: 1, pages: 1 };
    if (Array.isArray(value?.items)) return { items: value.items, total: value?.total ?? value.items.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
    return { items: [], total: 0, page: 1, pages: 1 };
  };

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const queryFilters = useMemo(() => {
    const params = { page, limit: pageSize };
    const trimmedSearch = debouncedSearch?.trim?.();
    if (trimmedSearch) params.search = trimmedSearch;
    if (actionFilter !== 'all') params.action = actionFilter;
    return params;
  }, [page, pageSize, debouncedSearch, actionFilter]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = normalizeListResponse(await auditApi.getAll(queryFilters));
        setAuditLogs(response.items);
        setTotalLogs(response.total);
        setTotalPages(response.pages);
        setPage((p) => clampPageToTotal(p, response.pages));
      } catch (err) {
        console.error('Failed to fetch audit logs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [queryFilters]);

  useEffect(() => {
    const fetchActions = async () => {
      try {
        const allLogs = await auditApi.getAll();
        const logs = Array.isArray(allLogs) ? allLogs : allLogs?.items || [];
        setAvailableActions([...new Set(logs.map((l) => l?.action).filter(Boolean))]);
      } catch (err) {
        console.error('Failed to fetch audit actions:', err);
      }
    };
    fetchActions();
  }, []);

  const actions = availableActions;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, actionFilter]);

  const actionColor = (action) => {
    if (action?.includes('Login') || action?.includes('Logout')) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (action?.includes('Added') || action?.includes('Approved') || action?.includes('Processed')) return 'bg-green-100 text-green-700 border-green-200';
    if (action?.includes('Rejected') || action?.includes('Deleted')) return 'bg-red-100 text-red-700 border-red-200';
    if (action?.includes('Updated') || action?.includes('Frozen') || action?.includes('Imported')) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="audit-trail-page" className="flex flex-col h-[calc(100vh-2rem)] md:h-[calc(100vh-3rem)] overflow-hidden animate-fadeIn">
      <div className="shrink-0 space-y-3 bg-background z-30">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-[Manrope]">Audit Trail</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Track all system actions and changes</p>
        </div>

        <Card className="border border-border shadow-sm">
          <CardContent className="p-3 sm:p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="audit-search"
                placeholder="Search by user, action, or details..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[200px]" data-testid="audit-action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      <Card className="flex-1 min-h-0 border border-border shadow-sm overflow-hidden flex flex-col mt-3">
        <CardContent className="p-0 flex-1 min-h-0">
          <Table containerClassName="h-full overflow-auto">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase tracking-widest font-medium w-[180px] sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Timestamp</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">User</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Action</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditLogs.map(log => {
                const Icon = actionIcons[log.action] || Shield;
                return (
                  <TableRow key={log.logId || log.id} data-testid={`audit-row-${log.logId || log.id}`}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#0047AB]/10 flex items-center justify-center text-[10px] font-bold text-[#0047AB]">
                          {log.userName?.charAt(0) || '?'}
                        </div>
                        <span className="text-sm font-medium">{log.userName || log.userId}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={actionColor(log.action)}>
                        <Icon className="w-3 h-3 mr-1" />
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[400px]">{log.details}</TableCell>
                  </TableRow>
                );
              })}
              {totalLogs === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                    <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    No audit logs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-3 pb-1">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({totalLogs} logs)
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="audit-page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page <= 1}
            data-testid="audit-prev-page-btn"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page >= totalPages}
            data-testid="audit-next-page-btn"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
